/**
 * GradeMonoidPanel — interactive ℤ/2-per-phase arithmetic in
 * G = ⊕_{s ∈ SDP} ℤ/2[Φ(s)].
 *
 * Two editable grade vectors, a read-only sum, preset cases, and a
 * small atlas of the seven ICG envelopes that live on the diagonals
 * `[s, η] + [s, ε]`. Mirrors chapter 0 §3 / chapter 3 §3.4.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { SDPS } from "../data/sdps.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import {
  componentKey,
  detectIcgEnvelope,
  formatGrade,
  gradeAdd,
  icgEnvelope,
  isNeutral,
  neutralGrade,
  singletonGrade,
  type Grade,
  type Phase,
  type SdpCode,
} from "../algorithms/grade.js";
import { GradeVector } from "../components/GradeVector.js";

interface SdpEntry {
  readonly code: SdpCode;
  readonly name: string;
  readonly icg: string;
  readonly color: string;
}

const SDPS_LOOKUP: readonly SdpEntry[] = SDPS.map((s) => ({
  code: s.code,
  name: s.name,
  icg: s.icg,
  color: s.color,
}));

function sdpColor(code: SdpCode): string {
  return SDPS_LOOKUP.find((s) => s.code === code)?.color ?? panelTokens.accent;
}

function toggleComponent(grade: Grade, sdp: SdpCode, phase: Phase): Grade {
  const next = { ...grade } as Record<string, 0 | 1>;
  const key = componentKey(sdp, phase);
  next[key] = ((grade[key] ?? 0) ^ 1) as 0 | 1;
  return Object.freeze(next);
}

interface Preset {
  readonly id: string;
  readonly label: string;
  readonly a: Grade;
  readonly b: Grade;
  readonly note: string;
}

function buildPresets(): readonly Preset[] {
  return [
    {
      id: "eta-dt-only",
      label: "η_Dt only",
      a: singletonGrade("Dt", "eta"),
      b: neutralGrade(),
      note: "A single grant in Dt; B is 0_G so the sum is just [Dt, η].",
    },
    {
      id: "self-cancel",
      label: "Self-cancel: [Tr, η] + [Tr, η]",
      a: singletonGrade("Tr", "eta"),
      b: singletonGrade("Tr", "eta"),
      note: "Same component added twice cancels: x + x = 0 in ℤ/2.",
    },
    {
      id: "icg-pa",
      label: "ICG-Pa envelope: [Pa, η] + [Pa, ε]",
      a: singletonGrade("Pa", "eta"),
      b: singletonGrade("Pa", "epsilon"),
      note: "Both phases of Pa — the composite hits the ICG-Pa diagonal.",
    },
    {
      id: "cross-sdp",
      label: "Cross-SDP composite: [Dt, η] + [Tr, ε]",
      a: singletonGrade("Dt", "eta"),
      b: singletonGrade("Tr", "epsilon"),
      note: "Distinct SDPs — a four-component vector that is not any ICG.",
    },
    {
      id: "reset",
      label: "Reset (both 0_G)",
      a: neutralGrade(),
      b: neutralGrade(),
      note: "Identity element of G.",
    },
  ];
}

export function GradeMonoidPanel(): React.JSX.Element {
  const [gradeA, setGradeA] = useState<Grade>(singletonGrade("Dt", "eta"));
  const [gradeB, setGradeB] = useState<Grade>(singletonGrade("Dt", "epsilon"));

  const sum = useMemo(() => gradeAdd(gradeA, gradeB), [gradeA, gradeB]);
  const icgFromSum = useMemo(() => detectIcgEnvelope(sum), [sum]);

  const presets = useMemo(buildPresets, []);

  const toggleA = useCallback((sdp: SdpCode, phase: Phase) => {
    setGradeA((g) => toggleComponent(g, sdp, phase));
  }, []);
  const toggleB = useCallback((sdp: SdpCode, phase: Phase) => {
    setGradeB((g) => toggleComponent(g, sdp, phase));
  }, []);

  const applyPreset = useCallback(
    (presetId: string) => {
      const preset = presets.find((p) => p.id === presetId);
      if (!preset) return;
      setGradeA(preset.a);
      setGradeB(preset.b);
    },
    [presets],
  );

  return (
    <PanelSurface.PanelShell
      title="Grade Monoid"
      subtitle="G = ⊕_s ℤ/2[Φ(s)] · ICG ENVELOPES ON THE SEVEN DIAGONALS"
      accentColor="#a78bfa"
    >
      <PanelSurface.Card glowColor="#a78bfa44">
        <PanelSurface.SectionHeading>Why ℤ/2 per phase?</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          The grade vector has <strong>fourteen components</strong>, one per{" "}
          <PanelSurface.InlineCode>(SDP, phase)</PanelSurface.InlineCode> pair: seven SDPs{" "}
          <PanelSurface.InlineCode>× 2</PanelSurface.InlineCode> phases (<PanelSurface.InlineCode>η</PanelSurface.InlineCode> grant,{" "}
          <PanelSurface.InlineCode>ε</PanelSurface.InlineCode>
          discharge). Each component is an element of{" "}
          <PanelSurface.InlineCode>ℤ/2</PanelSurface.InlineCode> — lit or not lit, nothing in between.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          Addition in <PanelSurface.InlineCode>G</PanelSurface.InlineCode> is componentwise XOR. The
          crucial fact: <PanelSurface.InlineCode>x + x = 0</PanelSurface.InlineCode> in{" "}
          <PanelSurface.InlineCode>ℤ/2</PanelSurface.InlineCode>. Lighting the same
          <PanelSurface.InlineCode>(SDP, phase)</PanelSurface.InlineCode> component a second time cancels
          it back to zero. This is what enforces{" "}
          <strong>single-shot semantics</strong> at the type level: replaying a
          discharged token does not produce a fresh authorization; it produces
          the neutral grade
          <PanelSurface.InlineCode>0_G</PanelSurface.InlineCode>, and by T1 that position has no
          inhabitant.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          The seven <strong>ICG envelopes</strong> are the diagonals{" "}
          <PanelSurface.InlineCode>[s, η] + [s, ε]</PanelSurface.InlineCode> — one for each SDP. Composing
          the grant and the discharge of the <em>same</em> SDP lands on the
          matching ICG envelope; composing across distinct SDPs lands somewhere
          else.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Arithmetic playground</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3.5 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          Click cells in either vector to toggle them. The third vector is the
          sum <PanelSurface.InlineCode>A + B ∈ G</PanelSurface.InlineCode>, recomputed instantly.
        </p>

        <div className="mb-4 flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-label={`Apply preset: ${preset.label}`}
              onClick={() => {
                applyPreset(preset.id);
              }}
              title={preset.note}
              className="cursor-pointer rounded-full border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.55)] px-3 py-2 text-[12.5px] font-semibold text-[var(--tcc-text)]"
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-[18px]">
          <PlaygroundRow
            title="Grade A · editable"
            accent={panelTokens.accentCyan}
            description="Toggle cells to build a grade vector for the left operand."
          >
            <GradeVector
              grade={gradeA}
              editable
              onToggle={toggleA}
              label="Grade A"
            />
          </PlaygroundRow>
          <PlaygroundRow
            title="Grade B · editable"
            accent={panelTokens.accentPink}
            description="Toggle cells to build a grade vector for the right operand."
          >
            <GradeVector
              grade={gradeB}
              editable
              onToggle={toggleB}
              label="Grade B"
            />
          </PlaygroundRow>
          <PlaygroundRow
            title="A + B in G · read-only"
            accent={panelTokens.accentPurple}
            description="Componentwise XOR. Self-cancellation visible when the same cell is lit in both A and B."
          >
            <GradeVector grade={sum} label="A + B" />
          </PlaygroundRow>
        </div>

        <div
          className="mt-[18px] flex flex-wrap gap-3"
          role="status"
          aria-live="polite"
        >
          <PanelSurface.KpiBox
            label="A grade"
            value={formatGrade(gradeA)}
            color={panelTokens.accentCyan}
          />
          <PanelSurface.KpiBox
            label="B grade"
            value={formatGrade(gradeB)}
            color={panelTokens.accentPink}
          />
          <PanelSurface.KpiBox
            label="A + B"
            value={formatGrade(sum)}
            color={isNeutral(sum) ? panelTokens.accentAmber : panelTokens.accentPurple}
            subtitle={
              isNeutral(sum) ? "self-cancelled to neutral grade" : undefined
            }
          />
          <PanelSurface.KpiBox
            label="ICG envelope?"
            value={icgFromSum?.icg ?? "none"}
            color={icgFromSum ? sdpColor(icgFromSum.sdp) : panelTokens.muted}
            subtitle={
              icgFromSum
                ? `composite hits the ${icgFromSum.sdp} diagonal`
                : "not on any single-SDP diagonal"
            }
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>The seven ICG envelopes</PanelSurface.SectionHeading>
        <p className="mt-2 mb-3.5 text-[13px] leading-[1.55] text-[var(--tcc-muted)]">
          One diagonal per SDP. Each is the grade of a single bridge that has
          carried both phases of one SDP. These seven envelopes are the only
          places where chapter 5's Case B composition can land.
        </p>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3">
          {SDPS_LOOKUP.map((sdp) => (
            <IcgRow key={sdp.code} sdp={sdp} />
          ))}
        </div>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>What this buys at the type level</PanelSurface.SectionHeading>
        <ul className="mt-2.5 ml-[18px] p-0 text-[13.5px] leading-[1.7]">
          <li>
            <strong>Self-cancellation → T1.</strong> A second discharge of the
            same component leaves the grade at <PanelSurface.InlineCode>0_G</PanelSurface.InlineCode>. T1
            says the neutral grade has no admissible bridge — so replay is
            unconstructable, not merely detected.
          </li>
          <li>
            <strong>Phase orthogonality → T2.</strong> Distinct elements of{" "}
            <PanelSurface.InlineCode>G</PanelSurface.InlineCode> are reflected as distinct grade types. A
            bridge body that witnesses one grade cannot witness another;
            presenting a token at a grade-distinct seam is refused at the
            type-checker.
          </li>
          <li>
            <strong>ICG diagonals → T3.</strong> Composition adds grades.
            Composing the η and ε of one SDP lands on that SDP's ICG envelope;
            composing across SDPs lands on a multi-component vector that is also{" "}
            <em>typed</em> — every composite has exactly one grade by the
            additivity of <PanelSurface.InlineCode>G</PanelSurface.InlineCode>.
          </li>
        </ul>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}

interface PlaygroundRowProps {
  readonly title: string;
  readonly accent: string;
  readonly description: string;
  readonly children: React.ReactNode;
}

function PlaygroundRow({
  title,
  accent,
  description,
  children,
}: PlaygroundRowProps): React.JSX.Element {
  return (
    <div
      className="rounded-xl border border-[var(--tcc-border)] px-4 py-3.5"
      style={{
        background: `linear-gradient(180deg, ${accent}0c, rgba(5,8,16,0.5))`,
      }}
    >
      <div className="mb-2.5 flex flex-col gap-1">
        <span
          className="font-mono text-[11.5px] font-bold uppercase tracking-[0.08em]"
          style={{ color: accent }}
        >
          {title}
        </span>
        <span className="text-[12px] leading-[1.5] text-[var(--tcc-muted)]">
          {description}
        </span>
      </div>
      {children}
    </div>
  );
}

interface IcgRowProps {
  readonly sdp: SdpEntry;
}

function IcgRow({ sdp }: IcgRowProps): React.JSX.Element {
  const envelope = useMemo(() => icgEnvelope(sdp.code), [sdp.code]);
  return (
    <div
      className="flex flex-col gap-2.5 rounded-xl border border-solid px-3.5 py-3"
      style={{
        borderColor: `${sdp.color}44`,
        background: `linear-gradient(135deg, ${sdp.color}12, transparent 80%)`,
      }}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <span
          aria-hidden
          className="font-mono text-[16px] font-extrabold"
          style={{
            color: sdp.color,
            textShadow: `0 0 14px ${sdp.color}77`,
          }}
        >
          {sdp.code}
        </span>
        <span className="text-[13px] font-bold text-[var(--tcc-bright)]">
          {sdp.name}
        </span>
        {PanelSurface.badge(sdp.icg, sdp.color)}
      </div>
      <div className="font-mono text-[11.5px] text-[var(--tcc-muted)]">
        [{sdp.code}, η] + [{sdp.code}, ε]
      </div>
      <GradeVector grade={envelope} compact label={`${sdp.icg} envelope`} />
    </div>
  );
}
